/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CreateRecordingProps,
  Recording,
  RecordingId,
  UpdateRecordingProps,
  FileEntity,
  FileId,
} from '@solo/app-core';
import { useEffect, useState, useRef } from 'react';
import { translate } from '@/app/i18n';
import { Button, Flex, RouterLink, Text } from '@/app/ui/atoms';
import { DragItem, DropItem, Icon, Modal } from '@/app/ui/molecules';
import { RecordingForm } from '@/app/ui';

interface RecordingTableComponentPorps {
  recordings: Array<Recording>;
  notEditableRecordingsIds?: RecordingId[];
  changeOrder: (recordingId: RecordingId, indexTo: number) => void;
  getAudioInfo: (audioId: FileId) => Promise<FileEntity>;
  deleteRecording?: (track: Recording) => void;
  createRecording: (recording: CreateRecordingProps) => Promise<void>;
  updateRecording: (recording: UpdateRecordingProps, recordingId: RecordingId) => Promise<void>;
  setNewAudio: (audio: File) => void;
  isDistributed?: boolean;
}

const RecordingTableComponent = (props: RecordingTableComponentPorps) => {
  const {
    recordings,
    notEditableRecordingsIds,
    changeOrder,
    getAudioInfo,
    deleteRecording,
    createRecording,
    updateRecording,
    setNewAudio,
    isDistributed,
  } = props;

  const [editRecording, setEditRecording] = useState<Recording>();

  return (
    <Flex.Container flex={1} className="table" flexDirection="column">
      <Flex.Container flex={1} className="thead">
        <Flex.Item flex={1} className="th" />
        <Flex.Item flex={2} className="th">
          {translate('TRACKS.ORDER')}
        </Flex.Item>
        <Flex.Item flex={7} className="th">
          {translate('TRACKS.TRACK_TITLE')}
        </Flex.Item>
        <Flex.Item flex={7} className="th">
          {translate('TRACKS.ARTIST')}
        </Flex.Item>
        <Flex.Item flex={2} className="th" />
      </Flex.Container>
      <Flex.Container className="tbody" flexDirection="column">
        {recordings.map((track, index) => (
          <DropItem
            canDrop={!isDistributed}
            key={track.id}
            drop={(recordingId) => {
              const draggedRecordingIndex = recordings.findIndex((r) => r.id === recordingId);

              if (draggedRecordingIndex !== -1) {
                const newOrder = [...recordings];
                const [movedTrack] = newOrder.splice(draggedRecordingIndex, 1);
                newOrder.splice(index, 0, movedTrack);

                newOrder.forEach((t, i) => {
                  // TODO: Improve this, since now is is changing the orderPosition of all the recordings every time we move one
                  changeOrder(t.id, i + 1);
                });
              }
            }}
            accept="ROW"
          >
            <DragItem text={`${track.id}`} type="ROW">
              <Flex.Container
                className="tr mb-2"
                alignItems="center"
                style={{
                  cursor: 'grab',
                }}
              >
                <Flex.Container className="td" flex={1} alignItems="center">
                  <Icon.DragAndDropIcon width={10} style={{ marginRight: 15, marginLeft: 15 }} />
                </Flex.Container>
                <Flex.Container className="td" flex={2} alignItems="center">
                  {index + 1}
                </Flex.Container>
                <Flex.Item flex={7} className="td">
                  {notEditableRecordingsIds === undefined ? (
                    <RouterLink $transparent $variant="primary" to={`${track.id}`}>
                      <Text.Paragraph fontWeight={600}>{track.title}</Text.Paragraph>
                    </RouterLink>
                  ) : (
                    <Text.Paragraph fontWeight={600}>{track.title}</Text.Paragraph>
                  )}
                </Flex.Item>
                <Flex.Item flex={7} className="td">
                  {track.artistName}
                </Flex.Item>
                <Flex.Item flex={2} className="td">
                  {!notEditableRecordingsIds?.includes(track.id) && !isDistributed && (
                    <Button onClick={() => setEditRecording(track)} $transparent>
                      <Icon.EditIcon width={18} style={{ marginRight: 15 }} />
                    </Button>
                  )}
                  {!isDistributed && (
                    <Button onClick={() => deleteRecording && deleteRecording(track)} $transparent>
                      <Icon.BinIcon width={16} />
                    </Button>
                  )}
                </Flex.Item>
              </Flex.Container>
            </DragItem>
          </DropItem>
        ))}
      </Flex.Container>
      <Modal
        isOpen={editRecording !== undefined}
        onClose={() => setEditRecording(undefined)}
        closeable
        position="top"
        size="lg"
      >
        <Flex.Container flexDirection="column" gap="40px" padding="30px 90px 20px 90px">
          <RecordingForm
            recordingData={
              editRecording
                ? ({
                    ...editRecording,
                    orderPosition:
                      editRecording.orderPosition && editRecording.orderPosition > 0
                        ? editRecording.orderPosition
                        : recordings.findIndex((recording) => recording.id === editRecording.id) +
                          1,
                  } as Recording)
                : undefined
            }
            closeForm={() => setEditRecording(undefined)}
            getAudioInfo={getAudioInfo}
            createRecording={createRecording}
            updateRecording={updateRecording}
            setNewAudio={(audio) => setNewAudio(audio)}
            existingTracksCount={recordings.length}
          />
        </Flex.Container>
      </Modal>
    </Flex.Container>
  );
};

export default RecordingTableComponent;
